import { Stack } from '@mui/material';
import { ChartsTooltipContainer } from '@mui/x-charts';
import {
  DatasetElementType,
  SeriesValueFormatter,
} from '@mui/x-charts/internals';
import { useTranslation } from '../../../../../services/translation/hooks/useTranslation';
import { BarChartTooltip } from '../../../components/ReportBarChart/components/BarChartTooltip';
import { EstimatedCountDisplay } from '../../../components/ReportBarChart/components/EstimatedCountDisplay';
import { StyledBarChart } from '../../../components/ReportBarChart/components/StyledBarChart';
import { useClusterReportChartData } from './useClusterReportChartData';

type Props = {
  scanDate: string;
  blockIds: string[];
  tableLoading: boolean;
  onBarClick?: (
    index: number,
    data: DatasetElementType<string | number>[]
  ) => void;
};

export const ClusterReportChart = ({
  scanDate,
  blockIds,
  tableLoading,
  onBarClick,
}: Props) => {
  const { data, yAxisValueFormatter } = useClusterReportChartData(
    scanDate,
    blockIds,
    tableLoading
  );

  const { t } = useTranslation();

  const tooltipFormatter: SeriesValueFormatter<number | null> = (
    value,
    { dataIndex }
  ) => {
    if (value === null) {
      return '';
    }

    const totalClusterCount = data?.at(dataIndex)?.totalClusterCount as
      | number
      | undefined;
    return totalClusterCount
      ? `${((value / totalClusterCount) * 100).toFixed(1)}%`
      : '';
  };

  return (
    <Stack spacing={4}>
      <EstimatedCountDisplay
        data={data ?? []}
        totalCountKey='filteredClusterCount'
      />
      <StyledBarChart
        series={[
          {
            id: 'filteredClusterCount',
            dataKey: 'filteredClusterCount',
            label: 'Filtered Cluster Count',
            color: '#711B99',
            stack: '1',
            valueFormatter: tooltipFormatter,
          },
          {
            id: 'differenceCount',
            dataKey: 'differenceCount',
            label: 'Difference',
            color: 'url(#diagonalHatch)',
            stack: '1',
            valueFormatter: () => '', // hide in tooltip,
          },
        ]}
        dataset={data ?? []}
        onItemClick={
          onBarClick
            ? (_, barItemIdentifier) =>
                onBarClick(barItemIdentifier.dataIndex, data)
            : undefined
        }
        height={data?.length ? data.length * 16 : 100}
        localeText={{
          noData: t('No data matches the selected filters'),
        }}
        yAxis={[
          {
            scaleType: 'band',
            dataKey: 'dataKey',
            valueFormatter: yAxisValueFormatter,
            disableTicks: true,
            categoryGapRatio: 0.5,
            width: 80,
          },
        ]}
        slots={{
          tooltip: () => (
            <ChartsTooltipContainer>
              <BarChartTooltip totalLabel={'Cluster Count'} />
            </ChartsTooltipContainer>
          ),
        }}
      >
        <pattern
          id='diagonalHatch'
          patternUnits='userSpaceOnUse'
          width='4'
          height='4'
          patternTransform='rotate(90 0 0)'
        >
          <path
            d='M-1,1 l2,-2
           M0,4 l4,-4
           M3,5 l2,-2'
            style={{ stroke: '#404040', strokeWidth: 1 }}
          />
        </pattern>
      </StyledBarChart>
    </Stack>
  );
};
